import { useEffect, useState } from "react";
import { 
    CreditCard, ShieldCheck, CheckCircle2, TrendingUp, Users, RefreshCw, 
    ArrowRight, Loader2, FileText, Check, AlertTriangle, HelpCircle, X
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { GlassCard, PageHeader, EmptyState } from "../components/shared";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function Billing() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [mockOpen, setMockOpen] = useState(false);
    const [mockOrder, setMockOrder] = useState(null);
    
    // Card simulation states
    const [cardNumber, setCardNumber] = useState("4111 1111 1111 1111");
    const [cardExpiry, setCardExpiry] = useState("12/28");
    const [cardCvv, setCardCvv] = useState("123");

    // Receipt overlay modal states
    const [receipt, setReceipt] = useState(null);

    const loadData = async () => {
        try {
            const [statusRes, txRes] = await Promise.all([
                api.get("/billing/status"),
                api.get("/billing/transactions")
            ]);
            setStatus(statusRes.data);
            setTransactions(txRes.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Load Razorpay official web SDK
    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handleUpgrade = async () => {
        setCheckoutLoading(true);
        try {
            const { data } = await api.post("/billing/create-order", { amount: 5900.00 });
            
            if (data.mode === "mock" || status?.payment_mode === "mock") {
                // Trigger simulated mockup modal only if explicitly in mock mode
                setMockOrder(data);
                setMockOpen(true);
            } else {
                // Real Razorpay payment gateway
                const loaded = await loadRazorpayScript();
                if (!loaded) {
                    toast.error("Failed to load Razorpay SDK. Please check your connection.");
                    setCheckoutLoading(false);
                    return;
                }
                
                const options = {
                    key: data.key_id || status.razorpay_key_id,
                    amount: data.amount * 100,
                    currency: "INR",
                    name: "ParshHR",
                    description: "Upgrade Workspace to Pro Plan",
                    order_id: data.order_id,
                    handler: async (response) => {
                        try {
                            const verifyRes = await api.post("/billing/verify-payment", {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                amount: 5900.00
                            });
                            toast.success(verifyRes.data.message || "Upgrade successful!");
                            loadData();
                        } catch (err) {
                            toast.error("Verification failed: " + formatApiError(err));
                        }
                    },
                    prefill: {
                        name: user?.name || "Workspace Admin",
                        email: user?.email || ""
                    },
                    theme: {
                        color: "#0D9488" // Teal accent color matching dashboard
                    }
                };
                
                const rzp = new window.Razorpay(options);
                rzp.open();
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCheckoutLoading(false);
        }
    };

    const submitMockPayment = async (success) => {
        setMockOpen(false);
        if (!success) {
            toast.error("Simulated payment cancelled/failed.");
            return;
        }

        setCheckoutLoading(true);
        try {
            const verifyRes = await api.post("/billing/verify-payment", {
                razorpay_order_id: mockOrder.order_id,
                razorpay_payment_id: `pay_mock_${Math.random().toString(36).substring(7)}`,
                razorpay_signature: "mock_signature_success",
                amount: 5900.00
            });
            toast.success(verifyRes.data.message || "Mock Upgrade Successful!");
            loadData();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCheckoutLoading(false);
        }
    };

    // Calculate total spend
    const totalSpend = transactions
        .filter(tx => tx.status === "paid")
        .reduce((sum, tx) => sum + tx.amount, 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <PageHeader 
                title="Billing & Subscriptions" 
                subtitle="Manage subscription tiers, payment gateway integrations, and invoices."
                actions={
                    <Button variant="outline" size="sm" onClick={loadData} className="rounded-full">
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                    </Button>
                }
            />

            {/* Metrics cards row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Plan status card */}
                <GlassCard className="p-5 flex items-center justify-between border-l-4 border-l-teal-500">
                    <div>
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Current Plan</span>
                        <div className="text-2xl font-bold font-heading mt-1 flex items-center gap-1.5 text-slate-800 dark:text-slate-100">
                            {status.plan === "pro" ? "Pro Premium" : "Free Trial"}
                            <span className={`w-2.5 h-2.5 rounded-full ${status.plan === "pro" ? "bg-emerald-500 animate-pulse" : "bg-amber-500"}`} />
                        </div>
                    </div>
                    <CreditCard className="w-7 h-7 text-teal-500 opacity-80" />
                </GlassCard>

                {/* Total spend card */}
                <GlassCard className="p-5 flex items-center justify-between border-l-4 border-l-indigo-500">
                    <div>
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Total Billed</span>
                        <div className="text-2xl font-bold font-heading mt-1 text-slate-800 dark:text-slate-100">
                            ₹{totalSpend.toLocaleString()}
                        </div>
                    </div>
                    <TrendingUp className="w-7 h-7 text-indigo-500 opacity-80" />
                </GlassCard>

                {/* Headcount limit card */}
                <GlassCard className="p-5 flex items-center justify-between border-l-4 border-l-sky-500">
                    <div>
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Active Headcount</span>
                        <div className="text-2xl font-bold font-heading mt-1 text-slate-800 dark:text-slate-100">
                            {status.active_employees_count} / {status.plan === "pro" ? "100" : "10"}
                        </div>
                    </div>
                    <Users className="w-7 h-7 text-sky-500 opacity-80" />
                </GlassCard>

                {/* Payment Gateway Mode */}
                <GlassCard className="p-5 flex items-center justify-between border-l-4 border-l-rose-500">
                    <div>
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-400">Gateway Mode</span>
                        <div className="text-2xl font-bold font-heading mt-1 text-slate-800 dark:text-slate-100 uppercase tracking-wider text-sm">
                            {status.payment_mode === "live" ? "Live (Razorpay)" : "Mock Simulator"}
                        </div>
                    </div>
                    <ShieldCheck className="w-7 h-7 text-rose-500 opacity-80" />
                </GlassCard>
            </div>

            {/* Upgrade & pricing tiers plan list */}
            {status.plan !== "pro" && (
                <div className="mt-8">
                    <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100 mb-6">Upgrade your Workspace</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
                        {/* Free plan */}
                        <GlassCard className="p-6 flex flex-col justify-between opacity-80">
                            <div>
                                <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-slate-850 text-slate-600 dark:text-slate-400">
                                    Current Plan
                                </span>
                                <h4 className="font-heading font-bold text-xl mt-3 text-slate-800 dark:text-slate-100">Free Trial</h4>
                                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                    Perfect for small teams and bootstrapping companies. Try out basic features.
                                </p>
                                <div className="text-2xl font-bold font-heading mt-4 text-slate-800 dark:text-slate-100">
                                    ₹0 <span className="text-xs font-normal text-muted-foreground">/ month</span>
                                </div>
                            </div>
                        </GlassCard>

                        {/* Pro plan upgrade option */}
                        <GlassCard className="p-6 relative border-2 border-teal-500/50 dark:border-teal-500/30 flex flex-col justify-between shadow-lg">
                            <div className="absolute -top-3.5 right-6 bg-teal-500 text-white text-[10px] uppercase font-bold tracking-wider px-3 py-1 rounded-full shadow">
                                Recommended
                            </div>
                            <div>
                                <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400">
                                    Upgrade
                                </span>
                                <h4 className="font-heading font-bold text-xl mt-3 text-slate-800 dark:text-slate-100">Pro Premium</h4>
                                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                    Unlock dynamic workspaces, real-time geofenced tracking logs, and advanced analytics dashboards.
                                </p>
                                <div className="text-2xl font-bold font-heading mt-4 text-slate-800 dark:text-slate-100">
                                    ₹5,000 + GST <span className="text-xs font-normal text-muted-foreground">/ month</span>
                                </div>
                            </div>
                            <ul className="space-y-2 text-xs text-slate-600 dark:text-slate-350 mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
                                <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal-500" /> <strong>Up to 100 employees & interns limit</strong></li>
                                <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal-500" /> Live active visitor tracker & IP logging</li>
                                <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal-500" /> Priority Helpdesk support setup</li>
                            </ul>
                            <div className="mt-8">
                                <Button 
                                    onClick={handleUpgrade} 
                                    disabled={checkoutLoading} 
                                    className="w-full rounded-full h-10 text-xs font-bold"
                                >
                                    {checkoutLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-3.5 h-3.5 mr-1" />}
                                    Upgrade workspace now
                                </Button>
                            </div>
                        </GlassCard>
                    </div>
                </div>
            )}

            {/* Pro Plan Active Card */}
            {status.plan === "pro" && (
                <GlassCard className="p-6 max-w-2xl border-l-4 border-l-emerald-500 bg-emerald-50/5 dark:bg-emerald-950/5">
                    <div className="flex items-start gap-4">
                        <CheckCircle2 className="w-8 h-8 text-emerald-500 shrink-0" />
                        <div>
                            <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Workspace is upgraded to Pro</h3>
                            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                                Thank you for your subscription! Your workspace is active with 100 member scale. Active attendance tracking, employee registration, and advanced analytics dashboards are fully unlocked.
                            </p>
                            <div className="flex flex-wrap gap-4 mt-4 text-xs font-semibold">
                                <span className="px-2.5 py-1 bg-emerald-100/50 dark:bg-emerald-950/20 text-emerald-600 dark:text-teal-400 rounded-full">
                                    Plan: 100 Members Cap
                                </span>
                                <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-350 rounded-full">
                                    Next billing: Auto-renew monthly
                                </span>
                            </div>
                        </div>
                    </div>
                </GlassCard>
            )}

            {/* Billing Transactions / History Table */}
            <div className="space-y-4">
                <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">Billing History & Invoices</h3>
                
                <GlassCard className="border border-slate-150 dark:border-slate-850">
                    {transactions.length === 0 ? (
                        <div className="p-8">
                            <EmptyState 
                                icon={FileText} 
                                title="No invoices available" 
                                description="Your subscription invoices will appear here once you upgrade." 
                            />
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs text-left">
                                <thead className="text-[10px] text-muted-foreground uppercase border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                                    <tr>
                                        <th className="p-3.5">Purchased By</th>
                                        <th className="p-3.5">Date</th>
                                        <th className="p-3.5">Plan</th>
                                        <th className="p-3.5">Order ID</th>
                                        <th className="p-3.5 text-right">Amount</th>
                                        <th className="p-3.5">Status</th>
                                        <th className="p-3.5 text-center">Invoice</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                                    {transactions.map((tx) => (
                                        <tr key={tx.id} className="text-slate-700 dark:text-slate-350">
                                            <td className="p-3.5 text-slate-900 dark:text-slate-100">{tx.user_name}</td>
                                            <td className="p-3.5">{new Date(tx.created_at).toLocaleDateString()}</td>
                                            <td className="p-3.5 uppercase text-[10px] font-bold tracking-wider">{tx.plan}</td>
                                            <td className="p-3.5 text-slate-500 font-mono">{tx.order_id}</td>
                                            <td className="p-3.5 text-right text-slate-900 dark:text-slate-100 font-bold">₹{tx.amount.toFixed(2)}</td>
                                            <td className="p-3.5">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                    tx.status === "paid" 
                                                        ? "bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400" 
                                                        : "bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400"
                                                }`}>
                                                    {tx.status}
                                                </span>
                                            </td>
                                            <td className="p-3.5 text-center">
                                                {tx.status === "paid" && (
                                                    <button 
                                                        onClick={() => setReceipt(tx)}
                                                        className="text-primary hover:text-teal-600 font-semibold flex items-center gap-1 mx-auto hover:underline"
                                                    >
                                                        <FileText className="w-3.5 h-3.5" /> View
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </GlassCard>
            </div>

            {/* Custom simulated card payment dialog overlay for Mock Mode */}
            {mockOpen && mockOrder && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <GlassCard className="w-full max-w-md p-6 border-t-4 border-t-teal-500 shadow-2xl relative bg-white dark:bg-slate-950">
                        <h3 className="font-heading font-bold text-lg mb-2 text-slate-800 dark:text-slate-100 flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-teal-500" />
                            ParshHR Mock Gateway
                        </h3>
                        <p className="text-[11px] text-muted-foreground leading-relaxed mb-4">
                            You are running in sandbox simulator. Submit mock details to simulate payment verification.
                        </p>

                        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-3.5 border border-slate-100 dark:border-slate-850 mb-5">
                            <div className="flex justify-between text-xs font-semibold">
                                <span className="text-slate-500">Order Reference:</span>
                                <span className="font-mono text-slate-800 dark:text-slate-200">{mockOrder.order_id}</span>
                            </div>
                            <div className="flex justify-between text-xs font-semibold mt-2">
                                <span className="text-slate-500">Amount Due:</span>
                                <span className="text-teal-600 dark:text-teal-400 font-bold">₹{mockOrder.amount.toFixed(2)}</span>
                            </div>
                        </div>

                        {/* Card input mockup fields */}
                        <div className="space-y-4 mb-6">
                            <div>
                                <Label className="text-[10px] uppercase font-bold tracking-wider">Card Number</Label>
                                <Input 
                                    type="text" 
                                    value={cardNumber} 
                                    onChange={(e) => setCardNumber(e.target.value)} 
                                    className="mt-1 font-mono text-xs" 
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-[10px] uppercase font-bold tracking-wider">Expiry Date</Label>
                                    <Input 
                                        type="text" 
                                        value={cardExpiry} 
                                        onChange={(e) => setCardExpiry(e.target.value)} 
                                        className="mt-1 font-mono text-xs" 
                                    />
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase font-bold tracking-wider">CVV Code</Label>
                                    <Input 
                                        type="password" 
                                        value={cardCvv} 
                                        onChange={(e) => setCardCvv(e.target.value)} 
                                        className="mt-1 font-mono text-xs" 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Simulated action triggers */}
                        <div className="grid grid-cols-2 gap-3">
                            <Button 
                                type="button" 
                                variant="outline"
                                onClick={() => submitMockPayment(false)}
                                className="rounded-full text-slate-500 hover:text-slate-700"
                            >
                                Cancel / Fail
                            </Button>
                            <Button 
                                type="button"
                                onClick={() => submitMockPayment(true)}
                                className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-bold text-white shadow-md shadow-emerald-600/10"
                            >
                                Pay Successful
                            </Button>
                        </div>
                    </GlassCard>
                </div>
            )}

            {/* Custom stylized PDF invoice receipt overlay modal */}
            {receipt && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <GlassCard className="w-full max-w-2xl p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl relative flex flex-col justify-between max-h-[90vh]">
                        {/* Close button */}
                        <button 
                            onClick={() => setReceipt(null)}
                            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        
                        {/* Invoice content for print/view */}
                        <div id="invoice-print-area" className="space-y-6 overflow-y-auto pr-2">
                            <div className="flex justify-between items-start border-b border-slate-100 dark:border-slate-800 pb-5">
                                <div>
                                    <h2 className="text-xl font-heading font-bold text-slate-900 dark:text-slate-100">ParshHR Invoice</h2>
                                    <p className="text-[10px] text-slate-500 mt-1">Invoice Receipt: #{receipt.id.substring(0, 8).toUpperCase()}</p>
                                </div>
                                <div className="text-right text-[10px] text-slate-500">
                                    <h4 className="font-bold text-slate-900 dark:text-slate-100">ParshWebCraft</h4>
                                    <p>Udaipur, Rajasthan, India</p>
                                    <p>admin@parshwebcraft.in</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-[10px]">
                                <div>
                                    <span className="text-slate-400 block font-bold uppercase tracking-wider">Billed To:</span>
                                    <strong className="text-slate-800 dark:text-slate-200 text-xs block mt-1">{receipt.user_name}</strong>
                                    <p className="text-slate-500 mt-0.5">Workspace Administrator</p>
                                </div>
                                <div className="text-right">
                                    <span className="text-slate-400 block font-bold uppercase tracking-wider">Payment Details:</span>
                                    <p className="mt-1"><strong className="text-slate-700 dark:text-slate-350">Date:</strong> {new Date(receipt.created_at).toLocaleDateString()}</p>
                                    <p className="mt-0.5"><strong className="text-slate-700 dark:text-slate-350">Payment ID:</strong> {receipt.payment_id}</p>
                                </div>
                            </div>

                            {/* Itemized Table */}
                            <table className="w-full text-[10px] text-left border border-slate-100 dark:border-slate-800">
                                <thead className="bg-slate-50 dark:bg-slate-950 font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100 dark:border-slate-850">
                                    <tr>
                                        <th className="p-3">Description</th>
                                        <th className="p-3 text-right">Quantity</th>
                                        <th className="p-3 text-right">Unit Price</th>
                                        <th className="p-3 text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-855">
                                    <tr className="text-slate-700 dark:text-slate-300">
                                        <td className="p-3 font-semibold">ParshHR Workspace Subscription - Pro Plan (Monthly Recurring)</td>
                                        <td className="p-3 text-right">1</td>
                                        <td className="p-3 text-right">₹{receipt.amount.toFixed(2)}</td>
                                        <td className="p-3 text-right font-bold">₹{receipt.amount.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className="flex justify-end pt-3">
                                <div className="w-64 space-y-1.5 text-xs text-right">
                                    <div className="flex justify-between text-[10px] text-slate-500">
                                        <span>Subtotal:</span>
                                        <span>₹{receipt.amount.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-[10px] text-slate-500">
                                        <span>GST (18% inclusive):</span>
                                        <span>₹{(receipt.amount * 0.18).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between font-bold border-t border-slate-100 dark:border-slate-800 pt-2 text-slate-800 dark:text-slate-100">
                                        <span>Total Paid:</span>
                                        <span>₹{receipt.amount.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Print Actions */}
                        <div className="flex justify-end gap-3 mt-6 border-t border-slate-100 dark:border-slate-800 pt-4">
                            <Button 
                                variant="outline" 
                                onClick={() => setReceipt(null)}
                                className="rounded-full"
                            >
                                Close
                            </Button>
                            <Button 
                                onClick={() => window.print()}
                                className="rounded-full bg-teal-650 hover:bg-teal-700 text-white font-bold"
                            >
                                <FileText className="w-3.5 h-3.5 mr-1" />
                                Print Receipt / Save PDF
                            </Button>
                        </div>
                    </GlassCard>
                </div>
            )}
        </div>
    );
}
